import notification from "antd/es/notification";
import { getLocation, push, RouterLocation } from "connected-react-router";
import { isNumber, omit } from "lodash-es";
import { defineMessages } from "react-intl";
import { Action } from "redux";
import { call, cancelled, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import { OrganizationSetting } from "@mapmycustomers/shared";
import DistanceUnit from "@mapmycustomers/shared/enum/DistanceUnit";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import PinLegendType from "@mapmycustomers/shared/enum/pinLegends/PinLegendType";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import {
  EntityType,
  EntityTypesSupportedByEmailFeature,
  EntityTypesSupportedByMapsPage,
  MapEntity,
} from "@mapmycustomers/shared/types/entity";
import {
  CategorizedMapEntries,
  EntityPin,
  MapEntitiesSet,
  MapPinsResponse,
  MapRecordsResponse,
  MultiPin,
} from "@mapmycustomers/shared/types/map";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import { MapPersistedRecordListConfiguration } from "@mapmycustomers/shared/types/map/types";
import Organization, { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import User from "@mapmycustomers/shared/types/User";
import { MapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import PlatformFilterModel, {
  PinLegendsFilter,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import PlatformSortModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformSortModel";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { isEntityPin } from "@mapmycustomers/shared/util/assert";

import {
  isAddressAreaSearchQuery,
  isCoordinatesAreaSearchQuery,
  isEntityAreaSearchQuery,
} from "@app/component/input/AreaSearchInput/utils/assert";
import getErrorNotificationDescription from "@app/component/map/core/utils/getErrorNotificationDescription";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import MapMode from "@app/enum/map/MapMode";
import Path from "@app/enum/Path";
import ReportType from "@app/enum/ReportType";
import LassoToolMode from "@app/scene/map/enums/LassoToolMode";
import {
  enterMode,
  fetchGroupLassoSelection,
  fetchLassoSelection,
  fetchTerritoryLassoSelection,
  selectMapTool,
  setChangesDiscarded,
  setGroupRecords,
  setRecords,
  setSelectionEntities,
  setTerritoryRecords,
  subscribeEmail,
  unsubscribeEmail,
  updateLassoClusters,
  updateViewportState,
} from "@app/scene/map/store/actions";
import {
  doesEntityViewHasChanges,
  getChangesDiscarded,
  getGroupMapViewState,
  getGroupRecords,
  getLassoMode,
  getListViewLocationQuery,
  getMapEntries,
  getMapMode,
  getMapModeBasePath,
  getMapRecordsListSortOrder,
  getMapViewport,
  getMapViewState,
  getMapViewTool,
  getMultiPin,
  getRecords,
  getRecordsListFilter,
  getRecordsListState,
  getSelectedEntities,
  getSelectedRecords,
  getTerritoryMapViewState,
  getTerritoryRecords,
  getVisibleLassoEntityTypes,
  isLastPinsRequestPinsOnly,
  isRecordsListFiltered,
  isSidebarVisible,
} from "@app/scene/map/store/selectors";
import { callApi } from "@app/store/api/callApi";
import { clearMapQuery, setMapPanelQuery, setMapQuery } from "@app/store/chat/viewContext/actions";
import { getEntityTypesWithExternalPreview } from "@app/store/entityView";
import { hideEntityView, showEntityView } from "@app/store/entityView/actions";
import { handleError } from "@app/store/errors/actions";
import { MAX_ITEMS_TO_DOWNLOAD_FILE } from "@app/store/exportEntities/const";
import {
  getCurrentUser,
  getFeatures,
  getOrganizationId,
  getOrganizationSettingValue,
  getOrganizationSize,
  getPosition,
} from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import AlertBadgesState from "@app/store/mapLayers/alerts/AlertBadgesState";
import { boundariesViewSettingsChanged } from "@app/store/mapLayers/boundaries/actions";
import { getAlertsData, isUserLocationSidebarVisible } from "@app/store/mapLayers/selectors";
import { colorOrShapeKeyChanged } from "@app/store/mapLayers/userLocations/actions";
import { getAllColorLegends, getAllShapeLegends } from "@app/store/pinLegends";
import { fetchPinLegends } from "@app/store/pinLegends/actions";
import convertMapFilterModelToFilterModelForEntity from "@app/store/savedFilters/convertMapFilterModelToFilterModel";
import { getSavedFilters } from "@app/store/savedFilters/selectors";
import { showSavedLocationCard } from "@app/store/savedLocationsUi";
import { AreaSearchQuery } from "@app/types/filters/AreaSearchQuery";
import MapViewportState from "@app/types/map/MapViewportState";
import Report from "@app/types/Report";
import getMapArea from "@app/util/chat/mapContext/getMapArea";
import getSavedFilterName from "@app/util/chat/pageContext/getSavedFilterName";
import isMapFilterApplied from "@app/util/chat/pageContext/isMapFilterApplied";
import toContextFilters from "@app/util/chat/pageContext/toContextFilters";
import { getLocalTimeZoneFormattedOffset } from "@app/util/dates";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { UniversalFieldName } from "@app/util/fieldModel/universalFieldsFieldModel";
import downloadEntitiesAsCsv from "@app/util/file/downloadEntitiesAsCsv";
import { formatDate } from "@app/util/formatters";
import categorizeMapEntries from "@app/util/map/categorizeMapEnties";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import convertViewportToPersist from "@app/util/map/convertViewportToPersist";
import getMapEntriesBounds from "@app/util/map/getMapEntriesBounds";
import getPrecisionDetails from "@app/util/map/getPrecisionDetails";
import { mapEntityIdGetter } from "@app/util/map/idGetters";
import { mapEntityIdParser } from "@app/util/map/idParsers";
import { toLeanPinsRequest } from "@app/util/map/leanPinsRequest";
import getColorShapeForEntity from "@app/util/map/markerStyles/getColorShapeForEntity";
import { getDefaultColorPinLegend, getDefaultShapePinLegend } from "@app/util/pinLegend";
import { getEntityTypeDisplayName } from "@app/util/ui";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import { MAP_RECORD_LIST_SCROLL_KEY } from "../../utils/consts";

import {
  addToSelection,
  applyMapViewSettings,
  applyRecordsListSettings,
  clearRecordsList,
  downloadMapRecords,
  fetchFilteredPinsBounds,
  fetchPins,
  fetchRecords,
  hideAnnotation,
  hideMapEntityView,
  hideSidebar,
  initializeMapMode,
  removeFromSelection,
  resetRecordsListPagination,
  setFocusedEntity,
  setPinHover,
  setSelection,
  showAnnotation,
  showMultiPinRecords,
  showSidebar,
  updateRecordsListCustomization,
} from "./actions";

const messages = defineMessages({
  dataFileTooLarge: {
    id: "map.recordList.exportFiles.dataFileTooLarge.warning",
    defaultMessage:
      "Data {multiple, select, true {files} other {file}} too large to download right now",
    description: "Message shown when sample companies are added",
  },
  dataFileTooLargeDescription: {
    id: "map.recordList.exportFiles.dataFileTooLarge.warning.description",
    defaultMessage:
      "Your export has been queued for processing and will be sent to your email once finished.",
    description: "Message shown when sample people are added",
  },
  error: {
    id: "map.recordList.exportFiles.error.message",
    defaultMessage: "Download Error",
    description: "Message shown when download fail",
  },
  errorDescription: {
    id: "map.recordList.exportFiles.error.description",
    defaultMessage:
      "There was a problem downloading your {entityType} data. Please try again. If this problem persists, please contact support@mapmycustomers.me.",
    description: "Description shown when download fail",
  },
  success: {
    id: "map.recordList.exportFiles.success",
    defaultMessage: "{multiple, select, true {Files} other {File}} Downloaded",
    description: "Message shown when sample people are added",
  },
});

function* onInitializeMapMode() {
  yield put(enterMode(undefined));
  yield put(initializeMapMode.success());
}

const hasAreaCondition = (filter: MapFilterModel): boolean =>
  !!filter.universal?.conditions.some(
    (condition) => "fieldFilterName" in condition && condition.operator === FilterOperator.IN_AREA
  );

export function* onFetchFilteredPinsBounds({
  payload,
}: ReturnType<typeof fetchFilteredPinsBounds.request>) {
  try {
    const mapViewState: MapViewState = yield select(getMapViewState);

    // The location filter's circle already fits the map to itself.
    if (hasAreaCondition(mapViewState.filter)) {
      yield put(fetchFilteredPinsBounds.success());
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: MapPinsResponse = yield callApi("fetchMapPins", orgId, {
      ...toLeanPinsRequest(
        {
          ...convertMapFilterToPlatformFilterModel(
            mapViewState.filter,
            mapViewState.visibleEntities
          ),
          precision: 5,
          precisionThreshold: 100,
        },
        { columns: ["id"] }
      ),
      $limit: 100,
    });

    yield call(payload.callback, getMapEntriesBounds(response.data));
    yield put(fetchFilteredPinsBounds.success());
  } catch (error) {
    yield put(fetchFilteredPinsBounds.failure());
    // No toast: the filter is applied either way, only the zoom is lost.
    yield put(handleError({ displayNotification: false, error }));
  }
}

export function* onFetchPins({ payload }: ReturnType<typeof fetchPins.request>) {
  let recordsListReloadReached = false;
  try {
    if (payload.viewport) {
      yield put(updateViewportState(payload.viewport));
      yield call(localSettings.updateViewportState, convertViewportToPersist(payload.viewport));
    }
    yield put(applyMapViewSettings(payload.request));
    const viewport: MapViewportState = yield select(getMapViewport);
    const mapViewState: MapViewState = yield select(getMapViewState);

    if (payload.request.boundaryKey) {
      yield put(boundariesViewSettingsChanged({ boundaryKey: payload.request.boundaryKey }));
    }

    // Skip the metadata write when payload.request is empty — that means
    // this is a viewport-only refetch (pan/zoom) and mapViewState didn't change.
    if (Object.keys(payload.request).length) {
      yield put(updateMetadata.request({ mapViewSettings2: omit(mapViewState, "lasso") }));
    }

    if (payload.updateOnly) {
      return;
    }

    const sidebarVisible: boolean = yield select(isSidebarVisible);
    const activeTool: MapTool | undefined = yield select(getMapViewTool);
    const lassoVisibleEntities: EntityTypesSupportedByMapsPage[] = yield select(
      getVisibleLassoEntityTypes
    );
    const lassoMode: LassoToolMode = yield select(getLassoMode);

    const isLassoMode = activeTool === MapTool.LASSO;
    const visibleEntityTypes =
      isLassoMode && lassoMode === LassoToolMode.ADD_GROUP_PINS
        ? lassoVisibleEntities
        : mapViewState.visibleEntities;

    const $offset = payload.request.range
      ? payload.request.range.startRow
      : mapViewState.range.startRow;
    const $limit = payload.request.range
      ? payload.request.range.endRow - payload.request.range.startRow
      : mapViewState.range.endRow - mapViewState.range.startRow;

    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const organizationSize: OrganizationSize = yield select(getOrganizationSize);
    const { visible: alertsVisible }: AlertBadgesState = yield select(getAlertsData);
    const filters: PlatformFilterModel = {
      bounds: viewport.bounds,
      pinLegends: MAP_ENTITY_TYPES.reduce(
        (result, entityType) => ({
          ...result,
          [entityType]: getColorShapeForEntity(
            mapViewState,
            colorPinLegends,
            shapePinLegends,
            entityType
          ),
        }),
        { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
      ),
      ...getPrecisionDetails(viewport.zoom ?? 1, organizationSize),
      ...convertMapFilterToPlatformFilterModel(mapViewState.filter, visibleEntityTypes),
    };
    const requestPayload = {
      ...toLeanPinsRequest(filters, { columns: ["name"], withBadges: alertsVisible }),
      $limit,
      $offset,
    };

    const response: MapPinsResponse = yield callApi("fetchMapPins", orgId, requestPayload);
    const savedFilters: SavedFilter[] | undefined = yield select(getSavedFilters, EntityType.PIN);
    const mapArea = getMapArea(mapViewState.filter);
    // Names the saved filter only while one of its conditions reaches the request; a Nearby counts.
    const sendsFilter = isMapFilterApplied(mapViewState.filter, {
      implicitFields: [],
      visibleTypes: visibleEntityTypes,
    });
    yield put(
      setMapQuery({
        entityTypes: visibleEntityTypes,
        filters: toContextFilters(requestPayload.$filters),
        // Only a named Nearby is the area; "No location" or an unnamed area reads "filtered".
        isFiltered: isMapFilterApplied(mapViewState.filter, {
          implicitFields: mapArea.kind === "near" ? [UniversalFieldName.GEOPOINT] : [],
          visibleTypes: visibleEntityTypes,
        }),
        nearbyPlace: mapArea.kind === "near" ? mapArea.name : undefined,
        savedFilterName: sendsFilter
          ? getSavedFilterName(savedFilters, mapViewState.selectedSavedFilterId)
          : undefined,
        totalCount: response.total,
      })
    );
    yield put(
      fetchPins.success({
        // if no visible entities, we still run request, but since it returns _all_ types
        // (see more about why here: https://mapmycustomers.slack.com/archives/C03PGD7BLBY/p1658160747808299?thread_ts=1658160472.031569&cid=C03PGD7BLBY
        //     This will go against the philosophy of how filters work on api today. By default,
        //     empty objects are ignored by api today. Making an exception for a certain endpoint
        //     would be problematic. I think you should not send an API request in such a case
        // )
        // we imitate like no pins were returned, however, still using total from the response
        ...categorizeMapEntries(visibleEntityTypes.length ? response.data : []),
        totalFilteredRecords: visibleEntityTypes.length ? response.total : 0,
      })
    );

    recordsListReloadReached = true;
    if (sidebarVisible && !payload.pinsOnly) {
      yield put(resetRecordsListPagination());
      yield put(fetchRecords.request(payload));
    }

    // Refresh status of clusters which are within lasso selection
    const pins: CategorizedMapEntries = yield select(getMapEntries);
    yield put(updateLassoClusters(pins.clusters));
  } catch (error) {
    yield put(clearMapQuery());
    yield put(fetchPins.failure(error));
    yield put(handleError({ error }));
  } finally {
    // A newer request cancelled this run before its records reload. A full one reloads the list
    // itself; a pins-only one does not, so this run reloads it to keep it matching the viewport.
    const wasCancelled: boolean = yield cancelled();
    if (wasCancelled && !payload.pinsOnly && !payload.updateOnly && !recordsListReloadReached) {
      const cancelledByPinsOnly: boolean = yield select(isLastPinsRequestPinsOnly);
      const sidebarVisible: boolean = yield select(isSidebarVisible);
      if (cancelledByPinsOnly && sidebarVisible) {
        yield put(resetRecordsListPagination());
        yield put(fetchRecords.request(payload));
      }
    }
  }
}

export function* onFetchRecords({ payload }: ReturnType<typeof fetchRecords.request>) {
  try {
    yield put(applyMapViewSettings(payload.request));
    if (payload.request.boundaryKey) {
      yield put(boundariesViewSettingsChanged({ boundaryKey: payload.request.boundaryKey }));
    }
    yield call(localSettings.clearRecordListScrollStateByKey, MAP_RECORD_LIST_SCROLL_KEY);

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewport: MapViewportState = yield select(getMapViewport);
    const mapViewState: MapViewState = yield select(getMapViewState);
    const recordsListState: MapViewState = yield select(getRecordsListState);
    const activeTool: MapTool | undefined = yield select(getMapViewTool);
    const selectedRecords: Set<string> = yield select(getSelectedRecords);
    const multiPin: MultiPin | undefined = yield select(getMultiPin);
    const isFiltered: boolean = yield select(isRecordsListFiltered);
    const searchFilter: string | undefined = yield select(getRecordsListFilter);
    const mode: MapMode = yield select(getMapMode);

    const groupMapViewState: MapViewState = yield select(getGroupMapViewState);
    const territoryMapViewState: MapViewState = yield select(getTerritoryMapViewState);
    const activeMapViewState =
      mode === MapMode.GROUPS
        ? groupMapViewState
        : mode === MapMode.TERRITORIES
          ? territoryMapViewState
          : mapViewState;

    const isLassoMode = activeTool === MapTool.LASSO;
    const isMultiPin = !!multiPin?.id;

    const lassoVisibleEntities: EntityTypesSupportedByMapsPage[] = yield select(
      getVisibleLassoEntityTypes
    );
    const lassoMode: LassoToolMode = yield select(getLassoMode);

    const visibleEntityTypes =
      isLassoMode && lassoMode === LassoToolMode.ADD_GROUP_PINS
        ? lassoVisibleEntities
        : activeMapViewState.visibleEntities;

    const $offset = payload.request.range
      ? payload.request.range.startRow
      : recordsListState.range.startRow;
    const $limit = payload.request.range
      ? payload.request.range.endRow - payload.request.range.startRow
      : recordsListState.range.endRow - recordsListState.range.startRow;

    if (visibleEntityTypes.length === 0) {
      yield put(
        fetchRecords.success({
          isFiltered,
          response: {
            accessible: 0,
            count: 0,
            data: [],
            limit: $limit,
            offset: 0,
            total: 0,
          },
        })
      );
      return;
    }
    const mapFilters = convertMapFilterToPlatformFilterModel(
      mapViewState.filter,
      visibleEntityTypes
    );

    const entities = mapFilters.entities as Record<
      EntityTypesSupportedByMapsPage,
      PlatformFilterModel
    >;
    Object.keys(entities).forEach((entityKey) => {
      entities[entityKey as EntityTypesSupportedByMapsPage].includeGroups = true;
    });

    if (searchFilter?.length) {
      // Inject filtering by name in records list
      if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
        const entities = mapFilters.entities as Record<
          EntityTypesSupportedByMapsPage,
          PlatformFilterModel
        >;
        (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
          if (!("$and" in entities[entityKey])) {
            entities[entityKey].$and = [];
          }
          const andCondition = entities[entityKey].$and;
          if (Array.isArray(andCondition)) {
            andCondition.push({ name: { $in: searchFilter } });
          }
        });
      }
    }

    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);

    if (isLassoMode && !isMultiPin && selectedRecords.size === 0) {
      // If we're in lasso, but haven't selected any records for whatever reason - it makes no sense
      // to request detailed list of records from backend
      yield put(clearRecordsList());
      return;
    }

    if (isLassoMode && !isMultiPin) {
      // Inject filtering by selected IDs
      if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
        const entityIdFilter: Record<EntityTypesSupportedByMapsPage, Array<Identified["id"]>> = {
          [EntityType.COMPANY]: [],
          [EntityType.DEAL]: [],
          [EntityType.PERSON]: [],
        };
        for (const mapEntityId of selectedRecords.values()) {
          const parsed = mapEntityIdParser(mapEntityId);
          if (parsed) {
            entityIdFilter[parsed.entity as EntityTypesSupportedByMapsPage].push(parsed.id);
          }
        }

        const entities = mapFilters.entities as Record<
          EntityTypesSupportedByMapsPage,
          PlatformFilterModel
        >;
        (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
          if (visibleEntityTypes.includes(entityKey)) {
            if (!("$and" in entities[entityKey])) {
              entities[entityKey].$and = [];
            }
            const andCondition = entities[entityKey].$and;
            if (Array.isArray(andCondition) && Array.isArray(entityIdFilter[entityKey])) {
              andCondition.push({ id: { $in: entityIdFilter[entityKey] } });
            }
          }
        });
      }
    }

    const bounds = isMultiPin ? multiPin.bounds : isLassoMode ? undefined : viewport.bounds;

    const pinLegendFilter = {
      pinLegends: MAP_ENTITY_TYPES.reduce(
        (result, entityType) => ({
          ...result,
          [entityType]: getColorShapeForEntity(
            mapViewState,
            colorPinLegends,
            shapePinLegends,
            entityType
          ),
        }),
        { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
      ),
    };

    let order;
    if (recordsListState.sort?.length > 0) {
      const sortField = recordsListState.sort[0].field;
      const sortOrder = recordsListState.sort[0].order;

      if (sortField?.sortName === UniversalFieldName.GEOPOINT) {
        const unit: DistanceUnit =
          (yield select(getOrganizationSettingValue, OrganizationSetting.DISTANCE_UNIT)) ??
          DistanceUnit.MILE;
        const query: AreaSearchQuery = yield select(getListViewLocationQuery);
        let coordinates: LongLat | undefined;
        if (isCoordinatesAreaSearchQuery(query) || isAddressAreaSearchQuery(query)) {
          coordinates = query.coordinates;
        } else if (isEntityAreaSearchQuery(query) && query.entity.geoPoint?.coordinates) {
          coordinates = query.entity.geoPoint?.coordinates;
        }
        if (coordinates) {
          order = {
            order: sortOrder === SortOrder.DESC ? "desc" : "asc",
            point: coordinates,
            unit,
          };
        }
      } else {
        order = convertToPlatformSortModel(recordsListState.sort);
      }
    }

    const requestPayload = {
      $filters: {
        ...mapFilters,
        ...pinLegendFilter,
        bounds,
        cadence: true,
        includeAccessStatus: true,
        includeAssociations: true,
        includeCustomFields: true,
        includeEmailSubscriptionStatus: true,
      },
      $limit,
      $offset,
      $order: order,
    };

    const response: MapRecordsResponse = yield callApi("fetchPins", orgId, requestPayload);
    if (isMultiPin && !isLassoMode) {
      yield put(
        setMapPanelQuery({
          filters: toContextFilters(requestPayload.$filters),
          multiPinId: multiPin.id,
          totalCount: response.total,
        })
      );
    }
    yield put(fetchRecords.success({ isFiltered, response }));
  } catch (error) {
    yield put(fetchRecords.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onShowMultiPinRecords({
  payload,
}: ReturnType<typeof showMultiPinRecords.request>) {
  try {
    yield put(showSidebar());
    yield put(hideMapEntityView());

    yield put(fetchRecords.request({ request: {} }));

    let address;

    if (isNumber(payload?.region?.longitude) && isNumber(payload?.region?.latitude)) {
      const orgId: Organization["id"] = yield select(getOrganizationId);

      const result: GeocodeResult = yield callApi("reverseGeocodeAddress", orgId, {
        coordinates: [payload.region!.longitude, payload.region!.latitude],
        type: "Point",
      });
      address = result.address.formattedAddress ?? "";
    }

    yield put(
      showMultiPinRecords.success({
        address,
      })
    );
  } catch (error) {
    yield put(showMultiPinRecords.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateRecordsListCustomization({
  payload,
}: ReturnType<typeof updateRecordsListCustomization.request>) {
  try {
    const recordsListSortOrder: SortModel = yield select(getMapRecordsListSortOrder);

    yield put(applyRecordsListSettings(payload));

    const recordListConfig: MapPersistedRecordListConfiguration = {
      sort: {
        field: payload.sort?.[0]?.field.name ?? recordsListSortOrder?.[0]?.field.name,
        order: payload.sort?.[0]?.order ?? recordsListSortOrder?.[0]?.order,
      },
    };
    yield put(updateMetadata.request({ mapRecordsListSettings: recordListConfig }));
    yield put(updateRecordsListCustomization.success());
  } catch (error) {
    yield put(updateRecordsListCustomization.failure());
    yield put(handleError({ error }));
  }
}

function* onShowEntityView({ payload }: ReturnType<typeof showEntityView>) {
  const entityTypesWithExternalPreview: EntityType[] = yield select(
    getEntityTypesWithExternalPreview
  );
  if (payload.entityType && !entityTypesWithExternalPreview.includes(payload.entityType)) {
    return;
  }

  const { pathname }: RouterLocation<any> = yield select(getLocation);
  const isOnMap = pathname.startsWith(Path.MAP);

  if (isOnMap) {
    const hasChanges: boolean = yield select(doesEntityViewHasChanges);

    const basePath: string = yield select(getMapModeBasePath);
    yield put(push(`${basePath}/entity/${payload.entityType}/${payload.entityId}`));

    if (hasChanges) {
      return;
    }
  }

  const isEntityMapped =
    payload.entityType &&
    [EntityType.COMPANY, EntityType.DEAL, EntityType.PERSON].includes(payload.entityType);
  const needToFocusOnPin = payload.focusOnPin && isOnMap && isEntityMapped;

  const mode: MapMode | undefined = yield select(getMapMode);

  // if we are on map and some special map mode is switched on we need to redirect user to new tab
  if (needToFocusOnPin && !!mode && payload.entityId && payload.entityType) {
    const url = `${window.location.origin}${Path.MAP}?previewEntityType=${
      payload.entityType
    }&previewEntityId=${payload.entityId}${payload.tab ? `&previewEntityTab=${payload.tab}` : ""}`;
    window.open(url, "_blank");
  } else {
    try {
      yield put(hideAnnotation());

      if (payload.entityId && payload.entityType) {
        yield put(setPinHover(`${payload.entityType}:${payload.entityId}`));
        // try to focus on pin
        if (needToFocusOnPin) {
          const orgId: Organization["id"] = yield select(getOrganizationId);
          const organizationSize: OrganizationSize = yield select(getOrganizationSize);
          const mapViewState: MapViewState = yield select(getMapViewState);
          const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
          const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);
          let focusedEntity;
          const requestPayload = toLeanPinsRequest(
            {
              entities: { [payload.entityType]: { $and: [{ id: payload.entityId }] } },
              pinLegends: MAP_ENTITY_TYPES.reduce(
                (result, entityType) => ({
                  ...result,
                  [entityType]: getColorShapeForEntity(
                    mapViewState,
                    colorPinLegends,
                    shapePinLegends,
                    entityType
                  ),
                }),
                { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
              ),
              precision: getPrecisionDetails(1, organizationSize).precision,
            },
            { columns: ["name"], withBadges: true }
          );

          const response: MapPinsResponse = yield callApi("fetchMapPins", orgId, requestPayload);
          if (response.data.length && isEntityPin(response.data[0])) {
            focusedEntity = response.data[0] as EntityPin;
          }
          yield put(setFocusedEntity(focusedEntity));
        }
      }
      yield put(showSidebar({ skipLoading: true }));
    } catch (error) {
      yield put(handleError({ error }));
    }
  }
}

export function* onHideMapEntityView() {
  try {
    const hasChanges: boolean = yield select(doesEntityViewHasChanges);

    const basePath: string = yield select(getMapModeBasePath);
    yield put(push(basePath));

    // A record opened from the Field View timeline forced the records list open to host the
    // record pane (onShowEntityView -> showSidebar). When the Field View side panel is open, close
    // the whole sidebar instead of only the record pane, so the user lands back on the Field View
    // panel rather than on the records list.
    const fieldViewSidebarOpen: boolean = yield select(isUserLocationSidebarVisible);
    const closeAction = fieldViewSidebarOpen ? hideSidebar() : hideEntityView();

    if (hasChanges) {
      yield take(setChangesDiscarded);

      const changesDiscarded: boolean = yield select(getChangesDiscarded);
      if (changesDiscarded) {
        yield put(closeAction);
      }
    } else {
      yield put(closeAction);
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onShowAnnotation({ payload }: ReturnType<typeof showAnnotation.request>) {
  try {
    const result = mapEntityIdParser(payload);
    if (!result) {
      // probably a lead pin. We don't fetch any data for lead annotations
      yield put(showAnnotation.failure());
      return;
    }

    const { id: entityId, entity: entityType } = result;

    // Try to find item in currently present record list first
    let record: MapEntity | undefined;
    const records: MapEntity[] = yield select(getRecords);
    if (Array.isArray(records) && records.length) {
      record = records.find(
        (entity) => mapEntityIdGetter(entity) === payload && entity.entity === entityType
      );
    }

    yield put(showAnnotation.success({ defaultEntity: record, entityId, entityType }));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(showAnnotation.failure());
  }
}

const getUpdatedRecordsList = (records: MapEntity[], selection: MapEntitiesSet) =>
  records.filter((record) => selection.has(mapEntityIdGetter(record)));

export function* onUpdateSelection(
  action: ReturnType<typeof addToSelection> | ReturnType<typeof removeFromSelection>
) {
  try {
    if (!action.payload) {
      return;
    }

    const isRemove = isActionOf(removeFromSelection, action);
    const sidebarVisible: boolean = yield select(isSidebarVisible);
    const selectedRecords: MapEntitiesSet = yield select(getSelectedRecords);

    const activeTool: MapTool | undefined = yield select(getMapViewTool);
    const isLassoMode = activeTool === MapTool.LASSO;

    const payload = action.payload instanceof Set ? action.payload : new Set([action.payload]);

    const newSelection = isRemove
      ? new Set(Array.from(selectedRecords).filter((value: string) => !payload.has(value)))
      : new Set([...payload, ...selectedRecords]);
    yield put(setSelection(newSelection));

    const mode: MapMode = yield select(getMapMode);

    // update selectedEntities w/o reloading for better UX
    if (isRemove && isLassoMode) {
      const selectedEntities: MapEntity[] = yield select(getSelectedEntities);
      yield put(setSelectionEntities(getUpdatedRecordsList(selectedEntities, newSelection)));

      switch (mode) {
        case MapMode.GROUPS:
          {
            const records: MapEntity[] = yield select(getGroupRecords);
            yield put(setGroupRecords(getUpdatedRecordsList(records, newSelection)));
          }
          break;

        case MapMode.TERRITORIES:
          {
            const records: MapEntity[] = yield select(getTerritoryRecords);
            yield put(setTerritoryRecords(getUpdatedRecordsList(records, newSelection)));
          }
          break;

        default: {
          const records: MapEntity[] = yield select(getRecords);
          yield put(setRecords(getUpdatedRecordsList(records, newSelection)));
        }
      }
    }

    if (newSelection.size && !sidebarVisible) {
      yield put(clearRecordsList());
      yield put(showSidebar());
    } else if (isLassoMode) {
      // do not fetch records in case of removal, coz we updated selectedEntities already
      if (!isRemove) {
        switch (mode) {
          case MapMode.GROUPS:
            yield put(fetchGroupLassoSelection.request({ request: {} }));
            break;

          case MapMode.TERRITORIES:
            yield put(fetchTerritoryLassoSelection.request({ request: {} }));
            break;

          default:
            yield put(fetchLassoSelection.request({ request: {} }));
        }
      }
    } else {
      yield put(resetRecordsListPagination());
      yield put(fetchRecords.request({ request: {} }));
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onHideSidebar() {
  try {
    yield put(hideEntityView());
    yield put(clearRecordsList());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

// Whoever opens a saved place's card, an earlier dropped pin doesn't stay open beside it
export function* onShowSavedLocationCard() {
  const tool: MapTool | undefined = yield select(getMapViewTool);
  if (tool === MapTool.DROP_PIN) {
    yield put(selectMapTool.request(undefined));
  }
}

export function* onDownloadMapRecords({ payload }: ReturnType<typeof downloadMapRecords>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUser: User = yield select(getCurrentUser);

    const viewport: MapViewportState = yield select(getMapViewport);
    const mapViewState: MapViewState = yield select(getMapViewState);
    const recordsListState: MapViewState = yield select(getRecordsListState);
    const activeTool: MapTool | undefined = yield select(getMapViewTool);
    const selectedRecords: Set<string> = yield select(getSelectedRecords);
    const multiPin: MultiPin | undefined = yield select(getMultiPin);
    const searchFilter = recordsListState.search?.trim();

    const features: OrganizationMetaData["features"] = yield select(getFeatures);
    const disallowColor = features?.[OrganizationFeature.DISALLOW_COLOR]?.enabled;

    const isLassoMode = activeTool === MapTool.LASSO;
    const isMultiPin = !!multiPin?.id;

    const lassoVisibleEntities: EntityTypesSupportedByMapsPage[] = yield select(
      getVisibleLassoEntityTypes
    );
    const lassoMode: LassoToolMode = yield select(getLassoMode);

    const visibleEntityTypes =
      isLassoMode && lassoMode === LassoToolMode.ADD_GROUP_PINS
        ? lassoVisibleEntities
        : mapViewState.visibleEntities;

    const mapFilters = convertMapFilterToPlatformFilterModel(
      mapViewState.filter,
      visibleEntityTypes
    );

    const entities = mapFilters.entities as Record<
      EntityTypesSupportedByMapsPage,
      PlatformFilterModel
    >;
    Object.keys(entities).forEach((entityKey) => {
      entities[entityKey as EntityTypesSupportedByMapsPage].includeGroups = true;
    });

    if (searchFilter?.length) {
      // Inject filtering by name in records list
      if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
        const entities = mapFilters.entities as Record<
          EntityTypesSupportedByMapsPage,
          PlatformFilterModel
        >;
        (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
          if (!("$and" in entities[entityKey])) {
            entities[entityKey].$and = [];
          }
          const andCondition = entities[entityKey].$and;
          if (Array.isArray(andCondition)) {
            andCondition.push({ name: { $in: searchFilter } });
          }
        });
      }
    }

    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);

    if (isLassoMode && selectedRecords.size === 0) {
      // If we're in lasso, but haven't selected any records for whatever reason - it makes no sense
      // to request detailed list of records from backend
      yield put(clearRecordsList());
      return;
    }

    if (isLassoMode && !isMultiPin) {
      // Inject filtering by selected IDs
      if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
        const entityIdFilter: Record<EntityTypesSupportedByMapsPage, Array<Identified["id"]>> = {
          [EntityType.COMPANY]: [],
          [EntityType.DEAL]: [],
          [EntityType.PERSON]: [],
        };
        for (const mapEntityId of selectedRecords.values()) {
          const parsed = mapEntityIdParser(mapEntityId);
          if (parsed) {
            entityIdFilter[parsed.entity as EntityTypesSupportedByMapsPage].push(parsed.id);
          }
        }

        const entities = mapFilters.entities as Record<
          EntityTypesSupportedByMapsPage,
          PlatformFilterModel
        >;
        (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
          if (visibleEntityTypes.includes(entityKey)) {
            if (!("$and" in entities[entityKey])) {
              entities[entityKey].$and = [];
            }
            const andCondition = entities[entityKey].$and;
            if (Array.isArray(andCondition) && Array.isArray(entityIdFilter[entityKey])) {
              andCondition.push({ id: { $in: entityIdFilter[entityKey] } });
            }
          }
        });
      }
    }

    const bounds = isMultiPin ? multiPin?.bounds : isLassoMode ? undefined : viewport.bounds;
    const pinLegendFilter = disallowColor
      ? {
          pinLegends: MAP_ENTITY_TYPES.reduce(
            (result, entityType) => ({
              ...result,
              [entityType]: getColorShapeForEntity(
                mapViewState,
                colorPinLegends,
                shapePinLegends,
                entityType
              ),
            }),
            { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
          ),
        }
      : {};

    let order: PlatformSortModel = undefined;
    if (recordsListState.sort?.length > 0) {
      const sortField = recordsListState.sort[0].field;
      const sortOrder = recordsListState.sort[0].order;

      if (sortField?.sortName === UniversalFieldName.GEOPOINT) {
        const unit: DistanceUnit =
          (yield select(getOrganizationSettingValue, OrganizationSetting.DISTANCE_UNIT)) ??
          DistanceUnit.MILE;
        const position: GeolocationPosition | undefined = yield select(getPosition);
        if (
          unit &&
          position?.coords?.longitude !== undefined &&
          position?.coords?.latitude !== undefined
        ) {
          order = {
            order: sortOrder === SortOrder.DESC ? "desc" : "asc",
            point: [position.coords.longitude, position.coords.latitude],
            unit,
          };
        }
      } else {
        order = convertToPlatformSortModel(recordsListState.sort);
      }
    }
    const intl = i18nService.getIntl();
    let entitiesWithResponseLimitCount = 0;
    let downloadedEntities = 0;
    for (const entityType of visibleEntityTypes) {
      const requiredEntity =
        mapFilters.entities &&
        (
          mapFilters.entities as Partial<
            Record<EntityTypesSupportedByMapsPage, PlatformFilterModel>
          >
        )[entityType];
      const checkFilters = { ...mapFilters, entities: { [entityType]: requiredEntity } };
      const requestPayload = {
        $filters: {
          ...checkFilters,
          ...pinLegendFilter,
          bounds,
          cadence: true,
          includeCustomFields: true,
        },
        $limit: 0,
        $order: order,
      };
      const columns =
        payload
          .get(entityType)
          ?.filter(({ visible }) => visible)
          .map(({ field }) => field) ?? [];
      const response: MapRecordsResponse = yield callApi("fetchPins", orgId, requestPayload);
      if (response.total > MAX_ITEMS_TO_DOWNLOAD_FILE) {
        const $filters = convertToPlatformFilterModel(
          convertMapFilterModelToFilterModelForEntity(entityType, mapViewState.filter),
          getFieldModelByEntityType(entityType).fields.map((field) => ({ field, visible: true })),
          getFieldModelByEntityType(entityType)
        );
        const selectedColumns = columns.map(({ exportColumnName }) => exportColumnName);
        const payload = {
          description: "",
          name: `${
            intl
              ? getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: false,
                })
              : entityType
          } ${formatDate(new Date(), "Pp")}`,
          selectedColumns,
          selectedFilters: {
            ...$filters,
            ...pinLegendFilter,
            bounds,
            cadence: true,
            includeCustomFields: true,
            includeEmailSubscriptionStatus: true,
          },
          tableName: entityType,
        };

        try {
          const report: Report = yield callApi("createReport", orgId, payload);
          yield callApi("generateReport", orgId, report.id, currentUser.username);
          entitiesWithResponseLimitCount += 1;
        } catch {
          notification.error({
            description: getErrorNotificationDescription(intl, payload.tableName),
            message: intl?.formatMessage(messages.error),
          });
        }
      } else {
        const response: MapRecordsResponse = yield callApi("fetchPins", orgId, {
          ...requestPayload,
          $limit: MAX_ITEMS_TO_DOWNLOAD_FILE,
        });
        if (response.data.length > 0) {
          let reportEntityType;
          switch (entityType) {
            case EntityType.COMPANY:
              reportEntityType = ReportType.COMPANIES;
              break;
            case EntityType.DEAL:
              reportEntityType = ReportType.DEALS;
              break;
            case EntityType.PERSON:
              reportEntityType = ReportType.PEOPLE;
              break;
          }
          downloadEntitiesAsCsv(
            `${reportEntityType}_preview.csv`,
            entityType,
            response.data,
            columns
          );
          downloadedEntities += 1;
        }
      }
    }
    if (intl) {
      if (downloadedEntities > 0) {
        notification.success({
          message: intl.formatMessage(messages.success, {
            multiple: downloadedEntities > 1,
          }),
        });
      }
      if (entitiesWithResponseLimitCount > 0) {
        notification.warning({
          description: intl.formatMessage(messages.dataFileTooLargeDescription),
          message: intl.formatMessage(messages.dataFileTooLarge, {
            multiple: entitiesWithResponseLimitCount > 1,
          }),
        });
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onCheckCurrentPinLegends({
  payload: allPinLegends,
}: ReturnType<typeof fetchPinLegends.success>) {
  const mapViewState: MapViewState = yield select(getMapViewState);

  let needUpdate = false;
  const colorKey = { ...mapViewState.colorKey };
  const shapeKey = { ...mapViewState.shapeKey };

  // Iterate over all currently selected color and shape pin legends and check
  // if they still exist. Replace by a default pin legend if not
  MAP_ENTITY_TYPES.forEach((entityType) => {
    if (colorKey[entityType]) {
      const colorPinLegendExists = allPinLegends.some(
        ({ id, type }) => id === colorKey[entityType] && type === PinLegendType.COLOR
      );
      if (!colorPinLegendExists) {
        const defaultColorPinLegend = getDefaultColorPinLegend(allPinLegends, entityType);
        colorKey[entityType] = defaultColorPinLegend?.id;
        needUpdate = true;
      }
    }
    if (shapeKey[entityType]) {
      const shapePinLegendExists = allPinLegends.some(
        ({ id, type }) => id === shapeKey[entityType] && type === PinLegendType.SHAPE
      );
      if (!shapePinLegendExists) {
        const defaultShapePinLegend = getDefaultShapePinLegend(allPinLegends, entityType);
        colorKey[entityType] = defaultShapePinLegend?.id;
        needUpdate = true;
      }
    }
  });

  if (needUpdate) {
    yield put(applyMapViewSettings({ colorKey, shapeKey }));
  }
}

export function* onSubscribeEmail({ payload }: ReturnType<typeof subscribeEmail.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi(
      "subscribeEntityEmail",
      orgId,
      payload.entity as EntityTypesSupportedByEmailFeature,
      payload.id
    );
    yield put(subscribeEmail.success(payload));
  } catch (error) {
    yield put(subscribeEmail.failure(payload));
    handleError({ error });
  }
}

export function* onUnsubscribeEmail({ payload }: ReturnType<typeof unsubscribeEmail.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi(
      "unsubscribeEntityEmail",
      orgId,
      payload.entity as EntityTypesSupportedByEmailFeature,
      payload.id
    );
    yield put(unsubscribeEmail.success(payload));
  } catch (error) {
    yield put(unsubscribeEmail.failure(payload));
    handleError({ error });
  }
}

export function* mapModeSagas() {
  yield takeLatest(initializeMapMode.request, onInitializeMapMode);
  yield takeLatest(
    (action: Action) => isActionOf(fetchPins.request)(action) && !action.payload.updateOnly,
    onFetchPins
  );
  yield takeEvery(
    (action: Action) => isActionOf(fetchPins.request)(action) && !!action.payload.updateOnly,
    onFetchPins
  );
  yield takeLatest(fetchRecords.request, onFetchRecords);
  yield takeLatest(fetchFilteredPinsBounds.request, onFetchFilteredPinsBounds);
  yield takeLatest(showMultiPinRecords.request, onShowMultiPinRecords);
  yield takeLatest(updateRecordsListCustomization.request, onUpdateRecordsListCustomization);
  yield takeLatest(showEntityView, onShowEntityView);
  yield takeLatest(hideMapEntityView, onHideMapEntityView);
  yield takeEvery(showSavedLocationCard, onShowSavedLocationCard);
  yield takeLatest([addToSelection, removeFromSelection], onUpdateSelection);
  yield takeLatest(hideSidebar, onHideSidebar);
  yield takeLatest(showAnnotation.request, onShowAnnotation);
  yield takeLatest(downloadMapRecords, onDownloadMapRecords);
  yield takeLatest(fetchPinLegends.success, onCheckCurrentPinLegends);
  yield takeLatest(subscribeEmail.request, onSubscribeEmail);
  yield takeLatest(unsubscribeEmail.request, onUnsubscribeEmail);
  yield takeLatest(
    (action: Action) =>
      isActionOf(applyMapViewSettings, action) &&
      (action.payload.colorKey !== undefined || action.payload.shapeKey !== undefined),
    function* () {
      // Read the keys from the reducer state rather than from the payload: the payload carries only the
      // entity type the user just changed, while the reducer has already merged it into the full map.
      const { colorKey, shapeKey }: MapViewState = yield select(getMapViewState);
      yield put(colorOrShapeKeyChanged({ colorKey, shapeKey }));
    }
  );
}
